import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { BottomNav } from "../components/Shell";
import WithdrawModal from "../components/WithdrawModal";
import {
  ArrowLeft, Loader2, Banknote, Wallet, TrendingUp, ArrowDownToLine,
  ArrowDownLeft, ArrowUpRight, Heart, Gift, RotateCcw, Receipt,
} from "lucide-react";

const TXN_UI = {
  deposit: { icon: ArrowDownLeft, label: "Deposit", sign: "+", color: "text-emerald-400", pill: "bg-emerald-500/15" },
  first_deposit_bonus: { icon: Gift, label: "First deposit bonus (15%)", sign: "+", color: "text-emerald-400", pill: "bg-emerald-500/15" },
  message_earning: { icon: Gift, label: "Message earning", sign: "+", color: "text-emerald-400", pill: "bg-emerald-500/15" },
  withdrawal_refund: { icon: RotateCcw, label: "Withdrawal refund", sign: "+", color: "text-sky-400", pill: "bg-sky-500/15" },
  message_spend: { icon: Heart, label: "Message credit used", sign: "-", color: "text-rose-400", pill: "bg-rose-500/15" },
  gift_spend: { icon: Gift, label: "Gift sent", sign: "-", color: "text-rose-400", pill: "bg-rose-500/15" },
  gift_earning: { icon: Gift, label: "Gift received", sign: "+", color: "text-emerald-400", pill: "bg-emerald-500/15" },
  withdrawal: { icon: ArrowUpRight, label: "Withdrawal", sign: "-", color: "text-amber-400", pill: "bg-amber-500/15" },
  platform_fee: { icon: Receipt, label: "Platform fee", sign: "-", color: "text-slate-400", pill: "bg-white/10" },
};

export default function Earnings() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const load = async () => {
    try {
      const [{ data: w }, { data: t }] = await Promise.all([
        api.get("/wallet"), api.get("/wallet/transactions"),
      ]);
      setWallet(w); setTxns(t.transactions);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totalEarned = txns.filter((t) => t.type === "message_earning").reduce((s, t) => s + t.amount, 0);
  const totalDeposited = txns.filter((t) => t.type === "deposit").reduce((s, t) => s + t.amount, 0);
  const earningCount = txns.filter((t) => t.type === "message_earning").length;

  return (
    <>
      <header className="h-16 px-3 bg-[#0D0C12]/90 backdrop-blur-xl border-b border-white/5 flex items-center gap-3 z-30 shrink-0">
        <button data-testid="earnings-back-button" onClick={() => navigate(-1)} className="w-9 h-9 grid place-items-center text-slate-300 rounded-full active:bg-white/10">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Earnings & History</h1>
      </header>

      <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-4" data-testid="earnings-page">
        {loading ? (
          <div className="h-40 grid place-items-center"><Loader2 className="w-7 h-7 text-rose-500 animate-spin" /></div>
        ) : (
          <>
            <div className="p-5 rounded-3xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/5 border border-emerald-400/30 mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300 flex items-center gap-1.5"><Banknote className="w-4 h-4" /> Withdrawal balance</p>
              <p data-testid="earnings-withdrawal-balance" className="text-4xl font-black text-white mt-1 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>₹{wallet?.withdrawal_balance ?? 0}</p>
              <button data-testid="earnings-withdraw-button" onClick={() => setShowWithdraw(true)}
                className="w-full h-11 rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold active:scale-[0.98] transition-transform flex items-center justify-center gap-2">
                <ArrowDownToLine className="w-4.5 h-4.5" /> Withdraw
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-5">
              <SummaryStat testid="earnings-total-earned" icon={TrendingUp} label="Total earned" value={`₹${totalEarned.toFixed(0)}`} accent="text-emerald-400" />
              <SummaryStat testid="earnings-count" icon={Gift} label="Paid chats" value={earningCount} accent="text-rose-400" />
              <SummaryStat testid="earnings-deposited" icon={Wallet} label="Deposited" value={`₹${totalDeposited.toFixed(0)}`} accent="text-sky-400" />
            </div>

            <h2 className="text-sm font-bold text-slate-300 mb-2 px-1">Transaction history</h2>
            {txns.length === 0 ? (
              <div className="py-12 text-center text-slate-500 text-sm" data-testid="earnings-empty">
                No transactions yet. Earn ₹{Math.round((wallet?.message_price || 100) * (wallet?.recipient_share_pct || 70) / 100)} every time someone messages you.
              </div>
            ) : (
              <div className="flex flex-col gap-2" data-testid="earnings-txn-list">
                {txns.map((t) => {
                  const ui = TXN_UI[t.type] || TXN_UI.platform_fee;
                  const Icon = ui.icon;
                  const label = (t.type === "gift_spend" || t.type === "gift_earning") && t.gift_emoji
                    ? `${ui.label} ${t.gift_emoji} ${t.gift_label || ""}`.trim()
                    : ui.label;
                  return (
                    <div key={t.txn_id} data-testid={`earnings-txn-${t.type}`} className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10">
                      <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${ui.pill}`}><Icon className={`w-4.5 h-4.5 ${ui.color}`} /></div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-white truncate">{label}</p>
                        <p className="text-[11px] text-slate-500">{new Date(t.created_at).toLocaleString()}{t.status && t.status !== "completed" ? ` · ${t.status}` : ""}</p>
                      </div>
                      <p className={`text-sm font-black shrink-0 ${ui.color}`}>{ui.sign}₹{t.amount}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <BottomNav />
      {showWithdraw && <WithdrawModal onClose={() => setShowWithdraw(false)} onSuccess={load} />}
    </>
  );
}

function SummaryStat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl bg-white/5 border border-white/10 p-3 flex flex-col items-center text-center">
      <Icon className={`w-4 h-4 mb-1 ${accent}`} />
      <span className="text-lg font-extrabold text-white leading-none" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</span>
      <span className="text-[10px] text-slate-500 mt-1">{label}</span>
    </div>
  );
}
